import React, { useState, useEffect } from 'react';
import { Container, Card, Button, Row, Col, Badge, Modal } from 'react-bootstrap';
import DeckBuilder from './DeckBuilder';
import DeckViewer from './DeckViewer';
import { 
  defaultDeck, 
  cardLookup, 
  CARD_TYPES,
  DECK_CONSTRAINTS,
} from '../CardData';

const SaveAsDeckModal = ({ show, onHide, onSave, existingDeckNames }) => {
  const [deckName, setDeckName] = useState('');
  const [error, setError] = useState('');

  const handleSave = () => {
	const trimmedName = deckName.trim();
	
	if (!trimmedName) {
	  setError('Please enter a deck name');
	  return;
	}

	if (trimmedName === 'Starter Deck') {
	  setError('Cannot use the name "Starter Deck"');
	  return;
	}

	if (existingDeckNames.includes(trimmedName)) {
	  setError('A deck with this name already exists');
	  return;
	}

	onSave(trimmedName);
	setDeckName('');
	setError('');
	onHide();
  };

  return (
	<Modal show={show} onHide={onHide} centered>
	  <Modal.Header closeButton>
		<Modal.Title>Save Deck As</Modal.Title>
	  </Modal.Header>
	  <Modal.Body>
		<div className="form-group">
		  <label>Deck Name:</label>
		  <input
			type="text"
			className="form-control"
			value={deckName}
			onChange={(e) => {
			  setDeckName(e.target.value);
			  setError('');
			}}
			placeholder="Enter deck name"
		  />
		  {error && <div className="text-danger mt-2">{error}</div>}
		</div>
	  </Modal.Body>
	  <Modal.Footer>
		<Button variant="secondary" onClick={onHide}>
		  Cancel
		</Button>
		<Button variant="primary" onClick={handleSave}>
		  Save Deck
		</Button>
	  </Modal.Footer>
	</Modal>
  );
};

const DeleteDeckModal = ({ show, onHide, onConfirm, deckName }) => (
  <Modal show={show} onHide={onHide} centered>
	<Modal.Header closeButton>
	  <Modal.Title>Delete Deck</Modal.Title>
	</Modal.Header>
	<Modal.Body>
	  Are you sure you want to delete "{deckName}"? This action cannot be undone.
	</Modal.Body>
	<Modal.Footer>
	  <Button variant="secondary" onClick={onHide}>
		Cancel
	  </Button>
	  <Button variant="danger" onClick={onConfirm}>
		Delete
	  </Button>
	</Modal.Footer>
  </Modal>
);

const DeckManager = ({ onExit }) => {
  const [decks, setDecks] = useState(() => {
	try {
	  const savedDecks = localStorage.getItem('savedDecks');
	  if (savedDecks) {
		const parsedDecks = JSON.parse(savedDecks);
		delete parsedDecks['Starter Deck'];
		return {
		  'Starter Deck': defaultDeck,
		  ...parsedDecks
		};
	  }
	  return { 'Starter Deck': defaultDeck };
	} catch (error) {
	  console.error('Error loading saved decks:', error);
	  return { 'Starter Deck': defaultDeck };
	}
  });

  const [currentView, setCurrentView] = useState('list');
  const [selectedDeck, setSelectedDeck] = useState(null);
  const [activeDeck, setActiveDeck] = useState(() => 
	localStorage.getItem('activeDeck') || 'Starter Deck'
  );
  const [showSaveAsModal, setShowSaveAsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deckToDelete, setDeckToDelete] = useState(null);
  const [saveAsDeckSource, setSaveAsDeckSource] = useState(null);

  // Debug logging
  useEffect(() => {
	console.log('Decks state:', decks);
  }, [decks]);

  useEffect(() => {
	const customDecks = { ...decks };
	delete customDecks['Starter Deck'];
	localStorage.setItem('savedDecks', JSON.stringify(customDecks));
  }, [decks]);

  const handleSaveDeck = (deckName, deckCards) => {
	console.log('DeckManager saving:', deckName, deckCards);

	if (deckName === 'Starter Deck') {
	  alert('Cannot modify Starter Deck');
	  return;
	}

	try {
	  if (!Array.isArray(deckCards) || deckCards.length === 0) {
		console.error('Invalid deck data:', deckCards);
		return;
	  }

	  // Convert cards to IDs and validate
	  const deckIds = deckCards.map(card => {
		if (typeof card === 'string') return card;
		if (!card || !card.id) {
		  console.error('Invalid card:', card);
		  return null;
		}
		return card.id;
	  }).filter(Boolean);

	  if (deckIds.length !== deckCards.length) {
		console.error('Some cards were invalid');
		return;
	  }

	  // Validate all IDs exist
	  if (!deckIds.every(id => cardLookup.has(id))) {
		console.error('Invalid card IDs in deck');
		return;
	  }

	  // Update state
	  setDecks(prevDecks => {
		const newDecks = {
		  ...prevDecks,
		  [deckName]: deckIds
		};
		return newDecks;
	  });

	  // Update active deck if needed
	  if (activeDeck === deckName) {
		localStorage.setItem('customDeck', JSON.stringify(deckIds));
	  }

	  // Return to deck list
	  setCurrentView('list');
	  setSelectedDeck(null);

	  console.log('Deck saved successfully');
	} catch (error) {
	  console.error('Error saving deck:', error);
	  alert('Error saving deck. Please try again.');
	}
  };

  const handleSetActive = (deckName) => {
	setActiveDeck(deckName);
	localStorage.setItem('activeDeck', deckName);
	const deckIds = decks[deckName];
	localStorage.setItem('customDeck', JSON.stringify(deckIds));
  };

  const handleDeleteConfirm = () => {
	if (!deckToDelete || deckToDelete === 'Starter Deck') return;

	const newDecks = { ...decks };
	delete newDecks[deckToDelete];
	setDecks(newDecks);

	if (activeDeck === deckToDelete) {
	  setActiveDeck('Starter Deck');
	  localStorage.setItem('activeDeck', 'Starter Deck');
	  localStorage.setItem('customDeck', JSON.stringify(defaultDeck));
	}

	setShowDeleteModal(false);
	setDeckToDelete(null);
  };

  const handleSaveAs = (newDeckName) => {
	if (!saveAsDeckSource) return;
	
	const sourceDeck = decks[saveAsDeckSource];
	if (!sourceDeck) {
	  console.error('Source deck not found:', saveAsDeckSource);
	  return;
	}

	// Convert IDs to full cards for saving
	const fullDeck = sourceDeck.map(id => cardLookup.get(id)).filter(Boolean);
	handleSaveDeck(newDeckName, fullDeck);
	setSaveAsDeckSource(null);
  };

  const getDeckStats = (deckIds) => {
	const deckCards = deckIds.map(id => cardLookup.get(id)).filter(Boolean);
	return {
	  total: deckCards.length,
	  types: {
		[CARD_TYPES.WISDOM]: deckCards.filter(card => card.type === CARD_TYPES.WISDOM).length,
		[CARD_TYPES.ECO]: deckCards.filter(card => card.type === CARD_TYPES.ECO).length,
		[CARD_TYPES.TECH]: deckCards.filter(card => card.type === CARD_TYPES.TECH).length
	  },
	  energy: deckCards.reduce((sum, card) => sum + (card.energy || 0), 0)
	};
  };

  const renderDeckList = () => (
	<Row>
	  {Object.entries(decks).map(([deckName, deckIds]) => {
		const stats = getDeckStats(deckIds);
		
		return (
		  <Col key={deckName} xs={12} md={6} lg={4} className="mb-3">
			<Card className="h-100">
			  <Card.Body className="d-flex flex-column">
				<Card.Title className="d-flex justify-content-between align-items-center">
				  <span>{deckName}</span>
				  {activeDeck === deckName && (
					<Badge bg="success">Active</Badge>
				  )}
				</Card.Title>
				
				<Card.Text>
				  <div className="d-flex gap-2 flex-wrap mb-2">
					<Badge bg={stats.total < DECK_CONSTRAINTS.MIN_CARDS ? 'danger' : 'primary'}>
					  Cards: {stats.total}
					</Badge>
					
				  </div>
				  <div className="d-flex gap-2 flex-wrap">
					<Badge bg="info">
					  Wisdom: {stats.types[CARD_TYPES.WISDOM]}
					</Badge>
					<Badge bg="success">
					  Eco: {stats.types[CARD_TYPES.ECO]}
					</Badge>
					<Badge bg="secondary">
					  Tech: {stats.types[CARD_TYPES.TECH]}
					</Badge>
				  </div>
				</Card.Text>

				<div className="mt-auto">
				  <div className="d-flex flex-wrap gap-2">
					<Button
					  variant="primary"
					  size="sm"
					  onClick={() => {
						setSelectedDeck(deckName);
						setCurrentView('view');
					  }}
					>
					  View
					</Button>

					{deckName === 'Starter Deck' ? (
					  <Button
						variant="primary"
						size="sm"
						onClick={() => {
						  setSaveAsDeckSource(deckName);
						  setShowSaveAsModal(true);
						}}
					  >
						Save As
					  </Button>
					) : (
					  <Button
						variant="warning"
						size="sm"
						onClick={() => {
						  setSelectedDeck(deckName);
						  setCurrentView('build');
						}}
					  >
						Edit
					  </Button>
					)}

					<Button
					  variant="success"
					  size="sm"
					  disabled={activeDeck === deckName}
					  onClick={() => handleSetActive(deckName)}
					>
					  Set Active
					</Button>

					{deckName !== 'Starter Deck' && (
					  <Button
						variant="danger"
						size="sm"
						onClick={() => {
						  setDeckToDelete(deckName);
						  setShowDeleteModal(true);
						}}
					  >
						Delete
					  </Button>
					)}
				  </div>
				</div>
			  </Card.Body>
			</Card>
		  </Col>
		);
	  })}
	</Row>
  );

  const renderContent = () => {
	switch (currentView) {
	  case 'build':
		return (
		  <DeckBuilder
			initialDeck={selectedDeck ? decks[selectedDeck] : []}
			initialDeckName={selectedDeck || ''}
			onSave={handleSaveDeck}
			onCancel={() => {
			  if (window.confirm('Are you sure you want to cancel? All changes will be lost.')) {
				setCurrentView('list');
				setSelectedDeck(null);
			  }
			}}
		  />
		);
	  case 'view':
		const viewDeck = decks[selectedDeck]?.map(id => cardLookup.get(id)).filter(Boolean);
		if (!viewDeck) {
		  console.error('Invalid deck for viewing:', selectedDeck);
		  setCurrentView('list');
		  return null;
		}
		return (
		  <DeckViewer
			deck={viewDeck}
			deckName={selectedDeck}
			onBack={() => {
			  setCurrentView('list');
			  setSelectedDeck(null);
			}}
			onSaveAs={selectedDeck === 'Starter Deck' ? () => {
			  setSaveAsDeckSource(selectedDeck);
			  setShowSaveAsModal(true);
			} : undefined}
		  />
		);
	  default:
		return (
		  <>
		  
			<div className="d-flex justify-content-between align-items-center mb-4" style={{paddingTop: '50px'}}>
			  <Button 
				variant="light" 
				onClick={onExit}
				
			  >
			  	<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-chevron-left" viewBox="0 0 16 16">
				  <path fillRule="evenodd" d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0"/>
				</svg>
				Back
			  </Button>
			  <Button 
				variant="success" 
								onClick={() => {
								  setSelectedDeck(null);
								  setCurrentView('build');
								}}
							  >
								+ New Deck
							  </Button>
							</div>
							{renderDeckList()}
						  </>
						);
					}
				  };
				
				  return (
					<div className="Home page-transition" style={{ minHeight: '100vh' }}>
						<Container className="py-4 blur2" style={{ paddingTop: '50px', paddingBottom: '100px',
						minHeight: '100vh'
						}}>
						  {renderContent()}
						</Container>
				
						<SaveAsDeckModal
						  show={showSaveAsModal}
						  onHide={() => {
							setShowSaveAsModal(false);
							setSaveAsDeckSource(null);
						  }}
						  onSave={handleSaveAs}
						  existingDeckNames={Object.keys(decks)}
						/>
				
						<DeleteDeckModal
						  show={showDeleteModal}
						  onHide={() => {
							setShowDeleteModal(false);
							setDeckToDelete(null);
						  }}
						  onConfirm={handleDeleteConfirm}
						  deckName={deckToDelete}
						/>
					  </div>
				  );
				};
				
				export default DeckManager;